import LayoutHR from "layouts/layout-hr";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import MasterDataFormV2 from "../MasterDataFormV2";
import { TimeOffRequestsProvider, useTimeOffRequests } from "./context/TimeOffRequestsContext";
import { getDevelopmentBanner } from "utils/devNotice";
import { useNavigate } from "react-router-dom";
import { deleteRequest, postRequest } from "services/api";
import { useUser } from "contexts/v1/masterData/UserContext";
import { useTimeOffPolicies } from "pages/hr/master/context/TimeOffPoliciesContext";
import { useMemo } from "react";

function TimeOffRequestsContent() {
  const { timeOffRequests, filterOptions, handleSearch, handleSearchChange, refetch, Pagination } =
    useTimeOffRequests();
  const { users } = useUser();
  const { policies } = useTimeOffPolicies();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const { handleError, handleSuccessToast } = useCanvasHandler();

  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setShowModal(true);
    setEditingItem(item);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/time-off-requests/${id}`);
        if (response.success) {
          handleSuccessToast(true, "Time off request deleted successfully");
          refetch();
        } else {
          handleError("Failed to delete time off request");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await postRequest(`/ahr/time-off-requests/${id}/approve`);
      if (response.success) {
        handleSuccessToast(true, "Time off request approved");
        refetch();
      } else {
        handleError(response.message || "Failed to approve");
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleReject = async (id) => {
    try {
      const response = await postRequest(`/ahr/time-off-requests/${id}/reject`);
      if (response.success) {
        handleSuccessToast(true, "Time off request rejected");
        refetch();
      } else {
        handleError(response.message || "Failed to reject");
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleCancel = async (id) => {
    try {
      const response = await postRequest(`/ahr/time-off-requests/${id}/cancel`);
      if (response.success) {
        handleSuccessToast(true, "Time off request cancelled");
        refetch();
      } else {
        handleError(response.message || "Failed to cancel");
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleTransformData = (data) => {
    const transformed = { ...data };
    
    // Format dates to "YYYY-MM-DDTHH:mm:ss.000000Z"
    const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      const isoString = d.toISOString(); // "YYYY-MM-DDTHH:mm:ss.sssZ"
      return isoString.replace(/\.\d{3}Z$/, ".000000Z");
    };

    if (transformed.start_date) transformed.start_date = formatDate(transformed.start_date);
    if (transformed.end_date) transformed.end_date = formatDate(transformed.end_date);
    
    return transformed;
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Time off request saved successfully");
  };

  const handleFormError = (error) => {
    handleError(error);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };
  
  const userOptions = useMemo(() => {
    return (
      users.data?.map((user) => ({
        value: user.id,
        label: `${user.name} (${user.email})`,
      })) || []
    );
  }, [users.data]);

  const policyOptions = useMemo(() => {
    return (
      policies.data?.map((policy) => ({
        value: policy.id,
        label: `${policy.name} (${policy.code})`,
      })) || []
    );
  }, [policies.data]);

  const formFields = [
    {
      name: "user_id",
      label: "User",
      type: "select",
      required: true,
      options: userOptions,
      placeholder: "Select User",
    },
    {
      name: "time_off_policy_id",
      label: "Policy",
      type: "select",
      required: true,
      options: policyOptions,
      placeholder: "Select Policy",
    },
    {
      name: "start_date",
      label: "Start Date",
      type: "date",
      required: true,
    },
    {
      name: "end_date",
      label: "End Date",
      type: "date",
      required: true,
    },
    {
      name: "duration",
      label: "Duration (Days)",
      type: "number",
      required: true,
      defaultValue: 1,
      min: 0.5,
      step: 0.5,
    },
    {
      name: "is_half_day",
      label: "Half Day",
      type: "checkbox",
    },
    {
      name: "half_day_type",
      label: "Half Day Type",
      type: "select",
      options: [
        { value: "morning", label: "Morning" },
        { value: "afternoon", label: "Afternoon" }
      ],
      placeholder: "Select Type (if Half Day)",
    },
    {
      name: "reason",
      label: "Reason",
      type: "textarea",
      required: true,
    },
    {
      name: "attachment",
      label: "Attachment",
      type: "text"
    },
    {
      name: "delegated_to",
      label: "Delegated To",
      type: "select",
      options: userOptions,
      placeholder: "Select User",
    },
  ];

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Time Off Requests</h2>
              <div className="text-muted mt-1">Manage employee leave requests</div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                Add Request
              </button>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search requests..."
                  value={filterOptions.search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearch}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Policy</th>
                    <th>Date Range</th>
                    <th>Duration</th>
                    <th>Delegate</th>
                    <th>Status</th>
                    <th>Approvals</th>
                  </tr>
                </thead>
                <tbody>
                  {timeOffRequests.loading ? (
                    <tr>
                      <td colSpan="7" className="text-center">Loading...</td>
                    </tr>
                  ) : timeOffRequests.data?.length > 0 ? (
                    timeOffRequests.data.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(filterOptions.currentPage - 1) * 10 + index + 1}</td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="default" size="sm">
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEdit(item)}>Edit</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleApprove(item.id)} className="text-success">Approve</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleReject(item.id)} className="text-warning">Reject</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleCancel(item.id)} className="text-secondary">Cancel</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleDelete(item.id)} className="text-danger">Delete</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{item.user?.name || item.user_id}</td>
                        <td>{item.policy?.name || item.time_off_policy_id}</td>
                        <td>
                          <div className="text-nowrap">{item.start_date}</div>
                          <div className="text-muted small">to {item.end_date}</div>
                        </td>
                        <td>
                          {item.duration} days
                          {item.is_half_day && (
                            <div className="badge bg-purple-lt ms-1">
                              {item.half_day_type}
                            </div>
                          )}
                        </td>
                        <td>{item.delegate?.name || "-"}</td>
                        <td>
                          <span className={`badge bg-${item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'danger' : 'warning'}`}>
                            {item.status}
                          </span>
                        </td>
                        <td>
                          {item.approvals?.length > 0 ? (
                            <div className="avatar-list avatar-list-stacked">
                              {item.approvals.map((app) => (
                                <span 
                                  key={app.id} 
                                  className={`avatar avatar-xs rounded-circle bg-${app.status === 'approved' ? 'green' : 'secondary'}`}
                                  title={`${app.approver?.name || 'Approver'} - ${app.status}`}
                                >
                                  {(app.approver?.name || 'A').charAt(0)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted small">No approvals</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">No requests found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {timeOffRequests.pagination && <Pagination />}
          </div>
        </div>

        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingItem ? "Edit Request" : "Create Request"}</h5>
                  <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/time-off-requests"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                    transformData={handleTransformData}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutHR>
  );
}

export default function TimeOffRequestsPage() {
  return (
    <TimeOffRequestsProvider>
      <TimeOffRequestsContent />
    </TimeOffRequestsProvider>
  );
}
